import { useEffect, useRef, useState } from 'react';

import { parseCsv } from '../helpers/csv.helper';

import type { ParsedCsv } from '../../AppAndSiteMarketplace.types';

/**
 * Reads and parses the CSV file in the browser. `onError` is called when the file cannot be read or has no
 * header/data rows; in that case the returned CSV stays `undefined`.
 */
export const useGenericCsvFile = (
  file: File | undefined,
  enabled: boolean,
  onError: () => void
): ParsedCsv | undefined => {
  const [csv, setCsv] = useState<ParsedCsv>();
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    setCsv(undefined);
    if (!enabled || !file) {
      return undefined;
    }
    let isCancelled = false;
    file
      .text()
      .then(parseCsv)
      .then(parsed => {
        if (isCancelled) {
          return;
        }
        if (parsed?.rows.length) {
          setCsv(parsed);
        } else {
          onErrorRef.current();
        }
      })
      .catch(() => {
        if (!isCancelled) {
          onErrorRef.current();
        }
      });
    return () => {
      isCancelled = true;
    };
  }, [file, enabled]);

  return csv;
};
